import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {loadTree, trackEvent} from './productfinder/api';
import Breadcrumbs from './productfinder/Breadcrumbs';
import ChoiceGrid from './productfinder/ChoiceGrid';
import MaterialHelp from './productfinder/MaterialHelp';
import {pushHistory, readStateFromUrl, seedHistory, syncCanonical} from './productfinder/history';
import Recommendations from './productfinder/Recommendations';
import Spinner from './productfinder/Spinner';
import {createTranslator, getLanguage} from './productfinder/i18n';
import {getAdvice, getAges, getAutomaticAge, getMaterials, getProblems, getRecommendations, getScopes} from './productfinder/tree';
import './css/productfinder.css';

/**
 * Leerer Auswahlzustand des Entscheidungsbaums.
 *
 * Die Codes werden bewusst als Zeichenketten gespeichert: JSON-Objektschlüssel
 * sind ebenfalls Zeichenketten. So vermeiden wir unklare Vergleiche zwischen
 * numerischen Backend-Codes und den Schlüsseln des geladenen Baums.
 */
const emptySelection = {scope: '', problem: '', age: '', material: ''};

/**
 * Zentrale Produktfinder-Anwendung.
 *
 * Die Komponente verbindet den Magento-Entscheidungsbaum mit einer kontrollierten
 * React- und Browser-History. Jeder fachliche Schritt erhält einen eigenen
 * History-Eintrag und eine sprechende, teilbare URL.
 */
export default function Produktfinder() {
  const language = useMemo(() => getLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  const [tree, setTree] = useState({});
  const [permalink, setPermalink] = useState({});
  const [selection, setSelection] = useState(emptySelection);
  const [step, setStep] = useState('area');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transitioning, setTransitioning] = useState(false);
  const [permalinkMessage, setPermalinkMessage] = useState('');
  const transitionTimer = useRef(null);

  useEffect(() => () => {
    if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
  }, []);

  /** Zeigt bei jedem Wechsel kurz eine sichtbare Aktivitätsbestätigung. */
  const showTransition = useCallback(() => {
    setTransitioning(true);
    if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
    transitionTimer.current = window.setTimeout(() => setTransitioning(false), 320);
  }, []);

  // Der statische, sprachspezifische Frontend-Cache wird bevorzugt. Der Magento-
  // Endpunkt fällt selbstständig auf die Datenbank zurück, wenn er noch fehlt.
  const fetchTree = useCallback(() => {
    setLoading(true);
    setError('');
    loadTree()
      .then((data) => {
        const nextTree = data.tree || {};
        const nextPermalink = data.permalink || {};
        setTree(nextTree);
        setPermalink(nextPermalink);
        // Erst nach dem Laden kann ein Permalink gegen die tatsächlich im Scope
        // verfügbaren Codes geprüft und als interne History aufgebaut werden.
        const restored = readStateFromUrl(nextTree, nextPermalink);
        const current = seedHistory(nextTree, nextPermalink, restored);
        setSelection(current.selection);
        setStep(current.step);
        trackEvent('start', current.selection);
        if (current.step === 'results') {
          trackEvent(getRecommendations(nextTree, current.selection).length ? 'results' : 'no_results', current.selection);
        }
      })
      .catch(() => setError(t('loadError')))
      .finally(() => setLoading(false));
  }, [t]);
  useEffect(fetchTree, [fetchTree]);

  useEffect(() => {
    if (!Object.keys(tree).length) return undefined;

    const onPopState = (event) => {
      // Eigene Einträge enthalten den bereits validierten Zustand. Falls ein
      // Browser oder Drittskript nur die URL wiederherstellt, wird sie erneut
      // strikt entlang des aktuellen Baums ausgewertet.
      const next = event.state?.kotoriProductfinder
        ? {selection: event.state.selection, step: event.state.step}
        : readStateFromUrl(tree, permalink);
      showTransition();
      setSelection(next.selection);
      setStep(next.step);
      setPermalinkMessage('');
      syncCanonical();
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [tree, permalink, showTransition]);

  // Jede Optionsliste wird ausschließlich aus dem aktuell erreichbaren Ast
  // abgeleitet. Nicht passende Auswahlmöglichkeiten erscheinen daher nie.
  const scopes = getScopes(tree);
  const problems = getProblems(tree, selection.scope);
  const ages = getAges(tree, selection.scope, selection.problem);
  const materials = getMaterials(tree, selection.scope, selection.problem, selection.age);
  const recommendations = getRecommendations(tree, selection);
  const advice = getAdvice(tree, selection);

  /** Aktualisiert React-Zustand, URL und Browser-History atomar. */
  const commitNavigation = (nextSelection, nextStep) => {
    showTransition();
    setSelection(nextSelection);
    setStep(nextStep);
    setPermalinkMessage('');
    pushHistory(tree, permalink, nextSelection, nextStep);
  };

  // Eine Änderung auf einer früheren Stufe verwirft alle abhängigen Werte. Die
  // anonyme Analyse erhält nur die fachlichen Codes und keinerlei Nutzerkennung.
  const chooseScope = (scope) => {
    const next = {scope, problem: '', age: '', material: ''};
    commitNavigation(next, 'problem');
    trackEvent('select_area', next);
  };
  const chooseProblem = (problem) => {
    const automaticAge = getAutomaticAge(getAges(tree, selection.scope, problem));
    const next = {...selection, problem, age: automaticAge || '', material: ''};
    commitNavigation(next, automaticAge !== null ? 'material' : 'age');
    trackEvent('select_problem', next);
  };
  const chooseAge = (age) => {
    const next = {...selection, age, material: ''};
    commitNavigation(next, 'material');
    trackEvent('select_age', next);
  };
  const chooseMaterial = (material) => {
    const next = {...selection, material};
    commitNavigation(next, 'results');
    trackEvent('select_material', next);
    trackEvent(getRecommendations(tree, next).length ? 'results' : 'no_results', next);
  };

  // Ein Breadcrumb erzeugt ebenfalls einen neuen, später vor- und rückwärts
  // erreichbaren Zustand. Der separate Zurück-Button nutzt dagegen history.back().
  const navigate = (target) => {
    if (target === 'area') commitNavigation(emptySelection, 'area');
    else if (target === 'problem') commitNavigation({...selection, problem: '', age: '', material: ''}, 'problem');
    else if (target === 'age') commitNavigation({...selection, age: '', material: ''}, 'age');
    else if (target === 'material') commitNavigation({...selection, material: ''}, 'material');
  };

  /** Kopiert den im Adressfeld bereits sichtbaren, sprechenden Ergebnislink. */
  const copyPermalink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setPermalinkMessage(t('linkCopied'));
      trackEvent('copy_permalink', selection);
    } catch (copyError) {
      setPermalinkMessage(t('linkCopyError'));
    }
  };

  // Breadcrumb-Texte stammen direkt aus dem geladenen Baum. Das automatisch
  // gewählte „Trifft nicht zu“ wird nicht als bewusste Nutzerauswahl gezeigt.
  const breadcrumbs = [];
  const scopeNode = scopes.find((item) => item.code === selection.scope);
  const problemNode = problems.find((item) => item.code === selection.problem);
  const ageNode = ages.find((item) => item.code === selection.age);
  const materialNode = materials.find((item) => item.code === selection.material);
  if (scopeNode) breadcrumbs.push({step: 'area', label: scopeNode.label});
  if (problemNode) breadcrumbs.push({step: 'problem', label: problemNode.label});
  if (ageNode && ageNode.code !== '0') breadcrumbs.push({step: 'age', label: ageNode.label});
  if (materialNode) breadcrumbs.push({step: 'material', label: materialNode.label});

  // Probleme und Materialien werden im jeweiligen Sprachkontext alphabetisch
  // sortiert. Die Bereichsreihenfolge bleibt durch den Baum vorgegeben.
  const sorted = (items) => [...items].sort((a, b) => a.label.localeCompare(b.label, language));

  if (loading) return <div className="kotori-productfinder"><Spinner label={t('loading')} /></div>;
  if (error) return <div className="kotori-productfinder"><p className="pf-error">{error}</p><button type="button" className="pf-button pf-button--primary" onClick={fetchTree}>{t('retry')}</button></div>;

  return (
    <main className="kotori-productfinder">
      {transitioning && <Spinner label={t('loading')} overlay />}
      <header className="pf-header">
        <h1>{t('title')}</h1>
        <p>{t('intro')}</p>
      </header>
      <Breadcrumbs items={breadcrumbs} title={t('yourSelection')} onNavigate={navigate} />

      <section className="pf-stage">
        {step === 'area' && <><h2>{t('areaQuestion')}</h2><ChoiceGrid kind="area" options={scopes} selected={selection.scope} onSelect={chooseScope} /></>}
        {step === 'problem' && <><h2>{t('problemQuestion')}</h2><ChoiceGrid kind="problem" options={sorted(problems)} selected={selection.problem} onSelect={chooseProblem} scope={selection.scope} /></>}
        {step === 'age' && <><h2>{t('ageQuestion')}</h2><ChoiceGrid kind="age" options={ages} selected={selection.age} onSelect={chooseAge} /></>}
        {step === 'material' && <><h2>{t('materialQuestion')}</h2><ChoiceGrid kind="material" options={sorted(materials)} selected={selection.material} onSelect={chooseMaterial} /><MaterialHelp language={language} /></>}
        {step === 'results' && <>
          <h2>{t('resultsTitle')}</h2>
          <p className="pf-results-intro">{t('resultsIntro')}</p>
          {advice && <div className="pf-advice">{advice}</div>}
          <div className="pf-permalink">
            <button type="button" className="pf-button" onClick={copyPermalink}>{t('copyLink')}</button>
            <span aria-live="polite">{permalinkMessage}</span>
          </div>
          <Recommendations recommendations={recommendations} language={language} t={t} selection={selection} onAnalytics={trackEvent} />
        </>}
      </section>

      {step !== 'area' && <button type="button" className="pf-button pf-button--back" onClick={() => { showTransition(); window.history.back(); }}>← {t('back')}</button>}
    </main>
  );
}
